import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static RESEND_COOLDOWN_MS = 60000
  static RESEND_COUNTDOWN_SECONDS = 60
  static OTP_LENGTH = 6

  static targets = ["otpInput", "message", "resendButton", "countdown"]
  static values = {
    verifyUrl: String,
    resendUrl: String
  }

  connect() {
    this.countdownInterval = null
    this.isVerifying = false

    // Focus first input
    if (this.hasOtpInputTarget && this.otpInputTargets[0]) {
      this.otpInputTargets[0].focus()
    }

    // Start resend countdown (code was just sent)
    this.startResendCountdown()
  }

  disconnect() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  }

  async verifyCode() {
    const code = this.otpInputTargets.map(input => input.value).join("")
    if (code.length !== this.constructor.OTP_LENGTH) {
      this.showMessage(`Please enter the complete ${this.constructor.OTP_LENGTH}-digit code`, "error")
      return
    }

    if (this.isVerifying) return
    this.isVerifying = true

    // Disable inputs during verification
    this.otpInputTargets.forEach(input => input.disabled = true)

    try {
      const response = await fetch(this.verifyUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({ code: code })
      })

      const data = await response.json()

      if (data.status === "success") {
        this.showMessage("Email verified! Creating your account...", "success")

        // Show success state on inputs
        this.otpInputTargets.forEach(input => {
          input.classList.remove("border-gray-300")
          input.classList.add("border-green-500", "bg-green-50")
        })

        // Redirect after short delay
        setTimeout(() => {
          if (data.redirect_url) {
            window.location.href = data.redirect_url
          } else {
            window.location.href = "/"
          }
        }, 1000)
      } else {
        this.showMessage(data.message || "That code didn't work. Please try again.", "error")
        this.showOtpError()
        this.isVerifying = false
        this.otpInputTargets.forEach(input => input.disabled = false)
      }
    } catch (error) {
      this.showMessage("Network error. Please try again.", "error")
      this.showOtpError()
      this.isVerifying = false
      this.otpInputTargets.forEach(input => input.disabled = false)
    }
  }

  async resendCode(event) {
    if (event) event.preventDefault()

    if (this.hasResendButtonTarget) {
      this.resendButtonTarget.disabled = true
    }

    try {
      const response = await fetch(this.resendUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        }
      })

      const data = await response.json()

      if (data.status === "success") {
        this.showMessage(data.message || "Verification code sent!", "success")
        this.clearOtpInputs()
        this.startResendCountdown()
      } else {
        this.showMessage(data.message || "Failed to resend code", "error")
        if (this.hasResendButtonTarget) {
          this.resendButtonTarget.disabled = false
        }
      }
    } catch (error) {
      this.showMessage("Network error. Please try again.", "error")
      if (this.hasResendButtonTarget) {
        this.resendButtonTarget.disabled = false
      }
    }
  }

  startResendCountdown(initialSeconds = this.constructor.RESEND_COUNTDOWN_SECONDS) {
    let seconds = initialSeconds

    if (this.hasResendButtonTarget) {
      this.resendButtonTarget.disabled = true
    }

    if (this.hasCountdownTarget) {
      this.countdownTarget.textContent = `(${seconds}s)`
    }

    this.countdownInterval = setInterval(() => {
      seconds--

      if (this.hasCountdownTarget) {
        this.countdownTarget.textContent = `(${seconds}s)`
      }

      if (seconds <= 0) {
        clearInterval(this.countdownInterval)

        if (this.hasResendButtonTarget) {
          this.resendButtonTarget.disabled = false
        }

        if (this.hasCountdownTarget) {
          this.countdownTarget.textContent = ""
        }
      }
    }, 1000)
  }

  handleOtpInput(event) {
    const input = event.target
    const index = this.otpInputTargets.indexOf(input)

    this.clearOtpError()
    input.value = input.value.replace(/[^0-9]/g, "")

    if (input.value.length === 1 && index < this.otpInputTargets.length - 1) {
      this.otpInputTargets[index + 1].focus()
    }

    // Auto-verify when complete
    if (this.isCodeComplete()) {
      setTimeout(() => this.verifyCode(), 100)
    }
  }

  handleOtpKeydown(event) {
    const input = event.target
    const index = this.otpInputTargets.indexOf(input)

    if (event.key === "Backspace" && input.value === "" && index > 0) {
      this.otpInputTargets[index - 1].focus()
    }

    if (event.key === "Enter") {
      event.preventDefault()
      if (this.isCodeComplete()) {
        this.verifyCode()
      }
    }
  }

  handleOtpPaste(event) {
    event.preventDefault()
    const paste = (event.clipboardData || window.clipboardData).getData("text")
    const digits = paste.replace(/[^0-9]/g, "").slice(0, this.constructor.OTP_LENGTH)

    digits.split("").forEach((digit, index) => {
      if (this.otpInputTargets[index]) {
        this.otpInputTargets[index].value = digit
      }
    })

    if (digits.length < this.constructor.OTP_LENGTH) {
      const nextEmpty = this.otpInputTargets.find(input => !input.value)
      if (nextEmpty) {
        nextEmpty.focus()
      }
    } else {
      this.otpInputTargets[this.constructor.OTP_LENGTH - 1].focus()
      // Auto-verify when paste completes the code
      setTimeout(() => this.verifyCode(), 100)
    }
  }

  isCodeComplete() {
    return this.hasOtpInputTarget &&
           this.otpInputTargets.length === this.constructor.OTP_LENGTH &&
           this.otpInputTargets.every(input => input.value.length === 1)
  }

  clearOtpInputs() {
    this.otpInputTargets.forEach(input => input.value = "")
    if (this.otpInputTargets[0]) {
      this.otpInputTargets[0].focus()
    }
  }

  showOtpError() {
    this.otpInputTargets.forEach(input => {
      input.classList.remove("border-gray-300", "focus:ring-[#2BC947]", "focus:border-[#2BC947]")
      input.classList.add("border-red-500", "text-red-600", "focus:ring-red-500", "focus:border-red-500", "bg-red-50")
    })
  }

  clearOtpError() {
    this.otpInputTargets.forEach(input => {
      input.classList.remove("border-red-500", "text-red-600", "focus:ring-red-500", "focus:border-red-500", "bg-red-50")
      input.classList.add("border-gray-300", "focus:ring-[#2BC947]", "focus:border-[#2BC947]")
    })

    if (this.hasMessageTarget && this.messageTarget.classList.contains("bg-red-50")) {
      this.messageTarget.classList.add("hidden")
    }
  }

  showMessage(text, type) {
    if (!this.hasMessageTarget) return

    this.messageTarget.classList.remove("hidden", "bg-green-50", "text-green-800", "border-green-200", "bg-red-50", "text-red-800", "border-red-200")

    if (type === "success") {
      this.messageTarget.textContent = text
      this.messageTarget.classList.add("bg-green-50", "text-green-800", "border-green-200")
    } else {
      this.messageTarget.innerHTML = `<div class="flex items-start gap-2">
        <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>${text}</span>
      </div>`
      this.messageTarget.classList.add("bg-red-50", "text-red-800", "border-red-200")
    }

    if (type === "success") {
      setTimeout(() => {
        this.messageTarget.classList.add("hidden")
      }, 5000)
    }
  }
}
